const PUBLIC_STREAM_EVENTS = new Set([
  "start",
  "conversation_created",
  "progress",
  "answer_reset",
  "answer_delta",
  "answer_done",
  "done",
  "error",
  "end",
]);
const TERMINAL_STREAM_EVENTS = new Set(["answer_done", "done", "error"]);

const ANSWER_DELTA_RENDER_CHARS = 24;

export const createStreamCompletionTracker = () => {
  let terminalEvent = "";
  return {
    observe(event) {
      if (event && TERMINAL_STREAM_EVENTS.has(event.event)) {
        terminalEvent = event.event;
      }
    },
    assertCompleted() {
      if (!terminalEvent) {
        const error = new Error("The AI response ended before completion.");
        error.code = "stream_incomplete";
        throw error;
      }
      return terminalEvent;
    },
  };
};

export const splitAnswerDelta = (value, maxChars = ANSWER_DELTA_RENDER_CHARS) => {
  const characters = Array.from(typeof value === "string" ? value : String(value || ""));
  if (!characters.length) return [];
  const size = Math.max(1, maxChars);
  const chunks = [];
  for (let index = 0; index < characters.length; index += size) {
    chunks.push(characters.slice(index, index + size).join(""));
  }
  return chunks;
};

const waitForNextPaint = () =>
  new Promise((resolve) => {
    if (
      typeof requestAnimationFrame === "function" &&
      (typeof document === "undefined" || document.visibilityState === "visible")
    ) {
      let settled = false;
      let frameId = null;
      let timeoutId = null;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (frameId !== null && typeof cancelAnimationFrame === "function") {
          cancelAnimationFrame(frameId);
        }
        if (timeoutId !== null) clearTimeout(timeoutId);
        resolve();
      };
      frameId = requestAnimationFrame(finish);
      timeoutId = setTimeout(finish, 50);
      return;
    }
    resolve();
  });

export const dispatchStreamEvent = async (event, onEvent) => {
  if (!event || !PUBLIC_STREAM_EVENTS.has(event.event)) return;
  if (event.event !== "answer_delta") {
    onEvent(event);
    return;
  }

  const chunks = splitAnswerDelta(
    event.delta ?? event.token ?? event.text ?? event.content ?? ""
  );
  for (const delta of chunks) {
    onEvent({ ...event, delta });
    // A single ReadableStream read may contain many SSE records. Give React
    // one paint opportunity between answer deltas so those records do not
    // collapse into the same visible state update.
    await waitForNextPaint();
  }
};

export const parseSseBlock = (block) => {
  const lines = block
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => line && !line.startsWith(":"));

  if (!lines.length) return null;

  const eventLine = lines.find((line) => line.startsWith("event:"));
  const eventName = eventLine ? eventLine.slice(6).trim() : "";
  const data = lines
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim())
    .join("\n");

  if (!data) return null;

  try {
    const parsed = JSON.parse(data);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return { ...parsed, event: parsed.event || eventName || "message" };
    }
    return { event: eventName || "message", data: parsed };
  } catch (error) {
    return { event: eventName || "message", data };
  }
};
